import { LazyArtworkIcon } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CopyLine,
  PdfLine,
  ShareArrowLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { $generateHtmlFromNodes } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { PAP_Click_ExportButton } from "@mirage/analytics/events/types/click_export_button";
import {
  $composeConvertToMarkdownString,
  composeConvertToHTMLString,
} from "@mirage/mosaics/ComposeAssistant/components/editor/ComposeMarkdownTransformers";
import { parseMarkdownContent } from "@mirage/mosaics/ComposeAssistant/data/markdown-utils";
import { createDoc } from "@mirage/service-cloud-docs";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import { openURL } from "@mirage/service-platform-actions";
import { toValidFileName } from "@mirage/service-stacks/service/utils";
import { hideSnackbar, showSnackbar } from "@mirage/shared/snackbar";
import { isWebOrDesktopEnvironment } from "@mirage/shared/utils/environment-detection";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { $getRoot } from "lexical";
import { memo, useCallback } from "react";
import styles from "./ComposeEditor.module.css";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { useIsConnectorConnected } from "@mirage/service-connectors/useConnectors";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";

const logger = tagged("ComposeEditor/ExportButton");
const ROOT_CSS_STYLES = `
<style>
* { line-height: 1.5em; font-family: Arial; margin: 0; }
strong { font-weight: 900 !important; }
li { padding-inline-start: 0em; }
ul, ol { padding-left: 0.75em; margin-bottom: 0em; } 
</style>
`;

interface ExportButtonProps {
  assistRequestId?: string;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  isSmallSize?: boolean;
}

const useShouldShowGoogleDocsExport = () => {
  const isGoogleDriveConnected = useIsConnectorConnected("googledrive");
  const isGoogleDriveEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_assist_2025_07_14_export_to_googledrive"),
  );
  return isGoogleDriveConnected && isGoogleDriveEnabled;
};

export const ExportButton = memo(
  ({ assistRequestId, logComposeEvent, isSmallSize }: ExportButtonProps) => {
    const [editor] = useLexicalComposerContext();
    const showExportToPaper = convertFeatureValueToBool(
      useFeatureFlagValue("dash_assist_2024_11_13_export_to_paper"),
    );
    const showExportToPDF = convertFeatureValueToBool(
      useFeatureFlagValue("dash_assist_2025_06_16_export_to_pdf"),
    );
    const showExportToGoogleDocs = useShouldShowGoogleDocsExport();

    const handleExportToPDF = async (title: string, htmlString: string) => {
      try {
        const { jsPDF } = await import("jspdf");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "pt",
          format: "a4",
        });
        pdf.html(
          `<div style="font-size: 9pt;">${ROOT_CSS_STYLES}${htmlString}</div>`,
          {
            margin: 72,
            autoPaging: "text",
            width: pdf.internal.pageSize.getWidth() - 144,
            windowWidth: pdf.internal.pageSize.getWidth() - 144,
            callback: (doc) => doc.save(`${title.trim()}.pdf`),
          },
        );
      } catch (err) {
        logger.error("failed to export to pdf", err);
        showSnackbar({
          title: i18n.t("compose_editor_export_error_saving_pdf_failed"),
        });
      }
    };

    const handleSelection = useCallback(
      (selection: "text" | "markdown" | "paper" | "pdf" | "googledocs") => {
        logComposeEvent(
          PAP_Click_ExportButton({
            actionType: selection,
            assistRequestId,
          }),
        );

        editor.getEditorState().read(() => {
          switch (selection) {
            case "text":
              {
                const htmlString = $generateHtmlFromNodes(editor, null);
                const textString = $getRoot().getTextContent();
                const data = [
                  new ClipboardItem({
                    "text/plain": new Blob([textString], {
                      type: "text/plain",
                    }),
                    "text/html": new Blob([htmlString], { type: "text/html" }),
                  }),
                ];
                navigator.clipboard.write(data);
                showSnackbar({
                  title: i18n.t("compose_editor_export_copied_completion"),
                });
              }
              break;
            case "markdown":
              {
                const markdownString = $composeConvertToMarkdownString({
                  includeSpecialAttributes: false,
                });
                const data = [
                  new ClipboardItem({
                    "text/plain": new Blob([markdownString], {
                      type: "text/plain",
                    }),
                  }),
                ];
                navigator.clipboard.write(data);
                showSnackbar({
                  title: i18n.t("compose_editor_export_copied_completion"),
                });
              }
              break;
            case "paper":
              {
                showSnackbar({
                  title: i18n.t("compose_editor_export_progress_saving_paper"),
                  showProgressBar: true,
                  timeoutMs: 0, // disable auto dismissal
                  hideCloseButton: true,
                });

                const markdownString = $composeConvertToMarkdownString({
                  includeSpecialAttributes: false,
                });
                const { title, body } = parseMarkdownContent(markdownString);
                const truncatedTitle = title
                  ? title.split(" ").slice(0, 20).join(" ") // first 20 words max
                  : "Untitled";
                const fileName = toValidFileName(truncatedTitle);
                createDoc("paper", fileName, {
                  import_format: {
                    ".tag": "markdown",
                  },
                  contents: body, // paper doc auto-adds file name as title to the resulting doc
                })
                  .then((response) => {
                    openURL(response.url);
                    hideSnackbar();
                    return undefined;
                  })
                  .catch((err) => {
                    logger.error("failed to create paper doc", err);
                    showSnackbar({
                      title: i18n.t("compose_editor_export_error_saving_paper"),
                    });
                  });
              }
              break;
            case "pdf":
              {
                if (!isWebOrDesktopEnvironment()) {
                  logger.warn(
                    "PDF export not available in extension environment",
                  );
                  showSnackbar({
                    title: i18n.t(
                      "compose_editor_export_error_saving_pdf_not_supported",
                    ),
                  });
                  return;
                }
                const { title } = parseMarkdownContent(
                  $composeConvertToMarkdownString({
                    includeSpecialAttributes: false,
                  }),
                );
                const htmlString = composeConvertToHTMLString(editor);
                handleExportToPDF(title, htmlString);
              }
              break;
            case "googledocs":
              {
                // TODO: add support for metadata like title, description, etc.
                showSnackbar({
                  title: i18n.t(
                    "compose_editor_export_progress_saving_google_docs",
                  ),
                  showProgressBar: true,
                  timeoutMs: 0, // disable auto dismissal
                  hideCloseButton: true,
                });
                const textString = $getRoot().getTextContent();
                const { title } = parseMarkdownContent(textString);
                const truncatedTitle = title
                  ? title.split(" ").slice(0, 20).join(" ") // first 20 words max
                  : "Untitled";
                const fileName = toValidFileName(truncatedTitle);
                createDoc("googledocs", `${fileName}.docx`, {
                  import_format: {
                    ".tag": "plain_text",
                  },
                  contents: textString,
                })
                  .then((response) => {
                    openURL(response.url);
                    hideSnackbar();
                    return undefined;
                  })
                  .catch((err) => {
                    logger.error("Failed to create google drive doc:", err);
                    showSnackbar({
                      title: i18n.t(
                        "compose_editor_export_error_saving_google_docs",
                      ),
                    });
                  });
              }
              break;
            default:
              selection satisfies never;
              logger.error("unsupported action", selection);
              break;
          }
        });
      },
      [editor, logComposeEvent],
    );
    return (
      <Menu.Wrapper onSelection={handleSelection}>
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              {...getTriggerProps()}
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ShareArrowLine} />}
              className={classNames(styles.editorExportButton, {
                [styles.editorExportButtonSmall]: isSmallSize,
              })}
            >
              {!isSmallSize && i18n.t("compose_editor_export_action")}
            </Button>
            <Menu.Content {...getContentProps()} placement="bottom-start">
              <Menu.Segment>
                <Menu.ActionItem
                  withTitle={i18n.t("compose_editor_export_action_copy_text")}
                  value="text"
                  withRightAccessory={<UIIcon src={CopyLine} />}
                />
                <Menu.ActionItem
                  withTitle={i18n.t(
                    "compose_editor_export_action_copy_markdown",
                  )}
                  value="markdown"
                  withRightAccessory={<UIIcon src={CopyLine} />}
                />
              </Menu.Segment>
              {showExportToPDF && (
                <Menu.Segment>
                  <Menu.ActionItem
                    withTitle={i18n.t("compose_editor_export_action_save_pdf")}
                    value="pdf"
                    withRightAccessory={<UIIcon src={PdfLine} />}
                  />
                </Menu.Segment>
              )}
              {showExportToPaper && isWebOrDesktopEnvironment() && (
                <Menu.Segment>
                  <Menu.ActionItem
                    withTitle={i18n.t(
                      "compose_editor_export_action_save_paper",
                    )}
                    value="paper"
                    withRightAccessory={<LazyArtworkIcon icon="Paper" />}
                  />
                </Menu.Segment>
              )}
              {showExportToGoogleDocs && (
                <Menu.Segment>
                  <Menu.ActionItem
                    withTitle={i18n.t(
                      "compose_editor_export_action_save_google_docs",
                    )}
                    value="googledocs"
                    withRightAccessory={<LazyArtworkIcon icon="GoogleDocs" />}
                  />
                </Menu.Segment>
              )}
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);

ExportButton.displayName = "ExportButton";
